// @ts-nocheck
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@libraryadmin/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import {
  FileDown,
  Printer,
  BarChart3,
  BookOpen,
  Users,
  ScrollText,
  Warehouse,
  Search,
  Calendar,
  Filter,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  getBooks,
  getManuscripts,
  getMembers,
  getBookIssues,
  getStorageLocations,
} from "@libraryadmin/services/api";

const CHART_COLORS = [
  "hsl(18, 74%, 30%)",
  "hsl(41, 58%, 48%)",
  "hsl(28, 60%, 52%)",
  "hsl(140, 28%, 38%)",
  "hsl(5, 60%, 40%)",
];

const Reports = () => {
  const [reportType, setReportType] = useState("book-issue");
  const [fromDate, setFromDate] = useState("2024-01-01");
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch queries
  const { data: books = [], isLoading: loadingBooks } = useQuery({
    queryKey: ["books"],
    queryFn: () => getBooks(),
  });
  const { data: manuscripts = [], isLoading: loadingMs } = useQuery({
    queryKey: ["manuscripts"],
    queryFn: getManuscripts,
  });
  const { data: members = [], isLoading: loadingMembers } = useQuery({
    queryKey: ["members"],
    queryFn: getMembers,
  });
  const { data: issues = [], isLoading: loadingIssues } = useQuery({
    queryKey: ["book-issues"],
    queryFn: getBookIssues,
  });
  const { data: storage = [], isLoading: loadingStorage } = useQuery({
    queryKey: ["storage-locations"],
    queryFn: getStorageLocations,
  });

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    if (reportType === "book-issue") {
      csvContent +=
        "Issue No,Member ID,Member Name,Book No,Book Title,Issue Date,Due Date,Status\n";
      issues.forEach(i => {
        csvContent += `"${i.issueNo}","${i.memberId}","${i.memberName}","${i.bookNo}","${i.bookTitle}","${i.issueDate}","${i.dueDate}","${i.status}"\n`;
      });
    } else if (reportType === "manuscript") {
      csvContent +=
        "Manuscript No,Title,Mulkarta,Tikakarta,Language,Script,Category,Condition,Digitalised,Restricted\n";
      manuscripts.forEach(m => {
        csvContent += `"${m.manuscriptNo}","${m.title}","${m.mulkarta || m.author}","${m.tikakarta || "-"}","${m.language}","${m.script}","${m.category}","${m.condition}","${m.digitalised ? "Yes" : "No"}","${m.restricted ? "Yes" : "No"}"\n`;
      });
    } else if (reportType === "member-usage") {
      csvContent +=
        "Member ID,Name,Phone,Email,City,Status,Books Issued,Joined Date\n";
      members.forEach(m => {
        csvContent += `"${m.memberId}","${m.name}","${m.phone}","${m.email}","${m.city}","${m.status}","${m.booksIssued}","${m.joinedDate}"\n`;
      });
    } else if (reportType === "storage") {
      csvContent +=
        "Code,Name,Type,Location Path,Item Count,Capacity,Occupancy Rate,Climate Controlled\n";
      storage.forEach(s => {
        const rate = Math.round((s.itemCount / s.capacity) * 100);
        csvContent += `"${s.code}","${s.name}","${s.type}","${s.locationPath}","${s.itemCount}","${s.capacity}","${rate}%","${s.temperatureControlled ? "Yes" : "No"}"\n`;
      });
    } else {
      csvContent +=
        "Book No,Title,Author,Publisher,Language,Location,Condition,Status\n";
      books.forEach(b => {
        csvContent += `"${b.bookNo}","${b.title}","${b.author}","${b.publisher}","${b.language}","${b.location}","${b.condition}","${b.status}"\n`;
      });
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `jambushrushti_${reportType}_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Chart aggregation data
  const manuscriptCategoryData = [
    {
      name: "Agama",
      value: manuscripts.filter(m => m.category === "Agama").length,
    },
    {
      name: "Philosophy",
      value: manuscripts.filter(m => m.category === "Philosophy").length,
    },
    {
      name: "Cosmology",
      value: manuscripts.filter(m => m.category === "Cosmology").length,
    },
    {
      name: "Ethics",
      value: manuscripts.filter(m => m.category === "Ethics").length,
    },
  ];

  const bookLanguageData = [
    {
      name: "English",
      value: books.filter(b => b.language === "English").length,
    },
    {
      name: "Sanskrit",
      value: books.filter(b => b.language === "Sanskrit").length,
    },
    {
      name: "Gujarati",
      value: books.filter(b => b.language === "Gujarati").length,
    },
    {
      name: "Hindi",
      value: books.filter(b => b.language === "Hindi").length,
    },
    {
      name: "Prakrit",
      value: books.filter(b => b.language === "Prakrit").length,
    },
  ];

  return (
    <DashboardLayout
      title="Reporting & Analytics Center"
      subtitle="Generate, analyze visual charts, and export library data reports"
    >
      <div className="space-y-6 print:m-0 print:p-0">
        {/* Report Generator Controls */}
        <div className="form-section print:hidden">
          <div className="mb-4 flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">
              Report Selector & Date Controls
            </h2>
          </div>

          <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-4">
            <div className="space-y-1.5">
              <Label>Report Module</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="input-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="book-issue">
                    Book Circulation & Issues
                  </SelectItem>
                  <SelectItem value="manuscript">
                    Manuscript Collection & Digitisation
                  </SelectItem>
                  <SelectItem value="member-usage">
                    Member Activity & Borrowing Trends
                  </SelectItem>
                  <SelectItem value="overdue">
                    Overdue Defaulters & Fines
                  </SelectItem>
                  <SelectItem value="storage">
                    Storage & Vault Utilization
                  </SelectItem>
                  <SelectItem value="catalog">
                    Full Book Catalog Inventory
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="input-field"
              />
            </div>

            <div className="space-y-1.5">
              <Label>To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                className="input-field"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={handleExportCSV} className="btn-primary flex-1">
                <FileDown className="mr-2 h-4 w-4" /> Export CSV
              </Button>
              <Button
                onClick={handlePrint}
                variant="outline"
                size="icon"
                className="shrink-0"
                title="Print / Export PDF"
              >
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Visual Trend Chart Section */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 print:hidden">
          <div className="card-elevated">
            <h3 className="mb-3 text-base font-semibold">
              Language Distribution
            </h3>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bookLanguageData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill={CHART_COLORS[0]}
                    radius={[4, 4, 0, 0]}
                    name="Books"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card-elevated">
            <h3 className="mb-3 text-base font-semibold">
              Manuscripts Category Share
            </h3>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={manuscriptCategoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={70}
                    dataKey="value"
                  >
                    {manuscriptCategoryData.map((_, idx) => (
                      <Cell
                        key={`c-${idx}`}
                        fill={CHART_COLORS[idx % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={24} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Report Preview Data Table */}
        <div className="form-section">
          <div className="mb-4 flex flex-col items-start justify-between gap-4 border-b pb-3 sm:flex-row sm:items-center">
            <div>
              <h3 className="text-lg font-bold capitalize text-foreground">
                {reportType.replace("-", " ")} Summary Report
              </h3>
              <p className="text-xs text-muted-foreground">
                Period: {fromDate} to {toDate} • Jambushrushti Library Official
                Record
              </p>
            </div>

            <div className="relative w-full sm:w-64 print:hidden">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search report entries..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="input-field pl-9 text-xs"
              />
            </div>
          </div>

          {/* Dynamic Table Preview */}
          <div className="overflow-hidden rounded-xl border border-border">
            {reportType === "book-issue" && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Issue No</TableHead>
                    <TableHead>Member Name</TableHead>
                    <TableHead>Book Title</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingIssues ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    issues
                      .filter(
                        i =>
                          i.memberName
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          i.bookTitle
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                      )
                      .map(i => (
                        <TableRow key={i.id}>
                          <TableCell className="font-mono text-xs font-semibold">
                            {i.issueNo}
                          </TableCell>
                          <TableCell className="text-sm font-semibold">
                            {i.memberName}
                          </TableCell>
                          <TableCell className="text-sm font-medium text-primary">
                            {i.bookTitle}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {i.issueDate}
                          </TableCell>
                          <TableCell className="text-xs font-semibold text-muted-foreground">
                            {i.dueDate}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                i.status === "overdue"
                                  ? "badge-destructive"
                                  : "badge-success"
                              }
                            >
                              {i.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            )}

            {reportType === "manuscript" && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>MS No</TableHead>
                    <TableHead>Granth Name</TableHead>
                    <TableHead>Mulkarta / Author</TableHead>
                    <TableHead>Language & Script</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Digitalised</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingMs ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    manuscripts
                      .filter(m =>
                        m.title
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase())
                      )
                      .map(m => (
                        <TableRow key={m.id}>
                          <TableCell className="font-mono text-xs font-semibold">
                            {m.manuscriptNo}
                          </TableCell>
                          <TableCell className="text-sm font-semibold text-primary">
                            {m.title}
                          </TableCell>
                          <TableCell className="text-xs">
                            {m.mulkarta || m.author}
                          </TableCell>
                          <TableCell className="text-xs">
                            {m.language} ({m.script})
                          </TableCell>
                          <TableCell className="text-xs">
                            {m.category}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                m.digitalised
                                  ? "badge-success"
                                  : "badge-secondary"
                              }
                            >
                              {m.digitalised ? "Yes" : "No"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            )}

            {reportType === "member-usage" && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Member ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Books Issued</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingMembers ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    members
                      .filter(m =>
                        m.name.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map(m => (
                        <TableRow key={m.id}>
                          <TableCell className="font-mono text-xs font-semibold">
                            {m.memberId}
                          </TableCell>
                          <TableCell className="text-sm font-semibold">
                            {m.name}
                          </TableCell>
                          <TableCell className="text-xs">{m.city}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {m.phone}
                          </TableCell>
                          <TableCell className="text-sm font-bold text-primary">
                            {m.booksIssued}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {m.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            )}

            {reportType === "overdue" && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Book No</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Issued To</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Days Overdue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingIssues ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    issues
                      .filter(i => i.status === "overdue")
                      .map(i => (
                        <TableRow key={i.id}>
                          <TableCell className="font-mono text-xs font-semibold">
                            {i.bookNo}
                          </TableCell>
                          <TableCell className="text-sm font-semibold text-primary">
                            {i.bookTitle}
                          </TableCell>
                          <TableCell className="text-sm">
                            {i.memberName} ({i.memberId})
                          </TableCell>
                          <TableCell className="text-xs font-semibold text-destructive">
                            {i.dueDate}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className="badge-destructive"
                            >
                              {Math.abs(i.daysRemaining || 0)} Days
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            )}

            {reportType === "storage" && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Code & Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location Path</TableHead>
                    <TableHead>Items / Capacity</TableHead>
                    <TableHead>Occupancy Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingStorage ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    storage.map(s => {
                      const rate = Math.round((s.itemCount / s.capacity) * 100);
                      return (
                        <TableRow key={s.id}>
                          <TableCell className="font-mono text-xs font-semibold">
                            {s.code} - {s.name}
                          </TableCell>
                          <TableCell className="text-xs capitalize">
                            {s.type}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {s.locationPath}
                          </TableCell>
                          <TableCell className="text-xs">
                            {s.itemCount} / {s.capacity}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                rate > 80 ? "badge-warning" : "badge-success"
                              }
                            >
                              {rate}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}

            {reportType === "catalog" && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Book No</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Language</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingBooks ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    books
                      .filter(b =>
                        b.title
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase())
                      )
                      .map(b => (
                        <TableRow key={b.id}>
                          <TableCell className="font-mono text-xs font-semibold">
                            {b.bookNo}
                          </TableCell>
                          <TableCell className="text-sm font-semibold text-primary">
                            {b.title}
                          </TableCell>
                          <TableCell className="text-xs">{b.author}</TableCell>
                          <TableCell className="text-xs">
                            {b.language}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {b.location}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {b.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Reports;
